<template>
  <div>
    <div v-if="$auth.policies['view-meal-config']" class="pa-4">
      <Breadcrumbs
        :headline="group.title || 'Chi tiết nhóm'"
        :link="[
          { text: 'Dịch vụ bếp', href: '#' },
          { text: 'Nhóm đăng ký ăn', href: '../meal-group' },
          { text: group.title || '' }
        ]"
      />

      <!-- Header card thông tin nhóm -->
      <v-card outlined class="px-4 py-6 my-6 elevation-0">
        <v-row class="align-center">
          <v-col cols="12" md="5">
            <div class="info-label">Nhóm đối tượng đăng ký ăn</div>
            <div class="info-value">{{ group.title }}</div>
          </v-col>
          <v-col cols="6" md="2">
            <div class="info-label">Phân loại</div>
            <div class="info-value">{{ groupCategoryLabel(group.category) }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="info-label">Số lượng học sinh</div>
            <div class="info-value">{{ totalItems }}</div>
          </v-col>
          <v-col cols="12" md="2" class="d-flex justify-end">
            <v-btn
              outlined
              class="v-btn--outlined theme--white v-size--small primary--text pa-4 font-weight-bold btn-color"
              depressed
              @click="infoDialog = true"
              >Hồ sơ chi tiết</v-btn
            >
          </v-col>
        </v-row>
      </v-card>

      <!-- Dialog HỒ SƠ CHI TIẾT (info, mọi nhóm) -->
      <v-dialog v-model="infoDialog" width="560" :fullscreen="$vuetify.breakpoint.smAndDown">
        <v-card>
          <v-card-title class="primary white--text">
            <span>HỒ SƠ CHI TIẾT</span>
            <v-spacer />
            <v-icon color="white" @click="infoDialog = false">close</v-icon>
          </v-card-title>
          <div class="pa-5">
            <v-row dense>
              <v-col cols="4" class="info-label">Nhóm đối tượng</v-col>
              <v-col cols="8">{{ group.title }}</v-col>
              <v-col cols="4" class="info-label">Phân loại</v-col>
              <v-col cols="8">{{ groupCategoryLabel(group.category) }}</v-col>
              <v-col cols="4" class="info-label">Số lượng học sinh</v-col>
              <v-col cols="8">{{ totalItems }}</v-col>
            </v-row>
          </div>
          <template v-if="group.category === 'custom'">
            <v-divider />
            <v-card-actions class="d-flex justify-end">
              <v-btn outlined color="primary" class="ma-2" @click="openRename">Sửa thông tin chi tiết</v-btn>
            </v-card-actions>
          </template>
        </v-card>
      </v-dialog>

      <!-- Dialog đổi tên nhóm -->
      <v-dialog v-model="renameDialog" width="480" :fullscreen="$vuetify.breakpoint.smAndDown">
        <v-card>
          <v-card-title class="primary white--text">
            <span>SỬA HỒ SƠ CHI TIẾT</span>
            <v-spacer />
            <v-icon color="white" @click="renameDialog = false">close</v-icon>
          </v-card-title>
          <v-form ref="renameForm" class="pa-4" @keydown.native.enter.prevent="saveRename">
            <v-text-field v-model="renameTitle" label="Nhóm đối tượng" outlined dense :rules="[$rules.required]" />
          </v-form>
          <v-card-actions>
            <v-btn outlined @click="renameDialog = false">Hủy</v-btn>
            <v-spacer />
            <v-btn color="primary" depressed :loading="savingRename" @click="saveRename">Lưu</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Tabs -->
      <v-tabs v-model="tab" active-class="tab-active" background-color="transparent">
        <v-tab class="tab-default">Danh sách học sinh</v-tab>
        <v-tab class="tab-default">Bữa ăn áp dụng</v-tab>
      </v-tabs>

      <v-card outlined class="elevation-0">
        <v-tabs-items v-model="tab">
          <!-- TAB 1: Danh sách học sinh -->
          <v-tab-item>
            <div class="d-flex justify-end pa-3">
              <v-btn outlined color="success" class="mx-1" @click="exportExcel">
                <v-icon left>mdi-file-excel</v-icon>Xuất Excel
              </v-btn>
              <v-btn
                v-if="group.category === 'custom'"
                depressed
                dark
                color="#0D47A1"
                class="mx-1"
                @click="$refs.importDialog.openDialog()"
              >
                <v-icon left>mdi-file-upload-outline</v-icon>Nhập Excel
              </v-btn>
              <v-btn
                v-if="group.category === 'custom'"
                depressed
                dark
                color="#0D47A1"
                class="mx-1"
                @click="$refs.addDialog.openDialog()"
              >
                <v-icon left>mdi-account-plus</v-icon>Thêm học sinh
              </v-btn>
            </div>

            <v-data-table
              :headers="studentHeaders"
              :items="students"
              :loading="loading"
              :server-items-length="totalItems"
              :options.sync="options"
              class="mx-3"
              :footer-props="{ 'items-per-page-options': [10, 20, 50], 'items-per-page-text': 'Số học sinh mỗi trang' }"
            >
              <template v-slot:[`item.stt`]="{ index }">{{
                (options.page - 1) * options.itemsPerPage + index + 1
              }}</template>
              <template v-slot:[`item.name`]="{ item }"><card-student-name :student="item" link/></template>
              <template v-slot:[`item.dob`]="{ item }">{{ item.dob | ddmmyyyy }}</template>
              <template v-slot:[`item.gender`]="{ item }">{{ item.gender | gender }}</template>
              <template v-slot:[`item.currentClass`]="{ item }">{{
                item.currentClass && item.currentClass.title
              }}</template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip v-if="item.status" small label class="white--text" :color="item.status | statusColor">
                  {{ item.status | status }}
                </v-chip>
              </template>
              <template v-slot:[`item.action`]="{ item }">
                <v-btn
                  v-if="group.category === 'custom' && item.groupStudentId"
                  icon
                  small
                  color="red"
                  @click="confirmRemove(item)"
                >
                  <v-icon small>delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>

            <v-col v-if="group.category === 'custom'" cols="12" class="d-flex justify-center">
              <v-btn outlined color="error" class="mt-2" @click="confirmDeleteGroup">Xoá nhóm đối tượng</v-btn>
            </v-col>
          </v-tab-item>

          <!-- TAB 2: Bữa ăn áp dụng cho nhóm -->
          <v-tab-item>
            <v-data-table
              :headers="mealHeaders"
              :items="appliedMeals"
              :loading="loadingMeals"
              class="mx-3"
              :items-per-page="10"
            >
              <template v-slot:no-data>
                <div class="my-10 font-weight-bold">Nhóm này chưa được áp dụng cho bữa ăn nào.</div>
              </template>
              <template v-slot:[`item.meal`]="{ item }">
                <router-link
                  v-if="item.meal_slot"
                  :to="`/meal-slots/${item.meal_slot.id}`"
                  class="text-decoration-none"
                >
                  {{ item.meal_slot.title }}
                </router-link>
              </template>
              <template v-slot:[`item.timeOfDay`]="{ item }">{{
                item.meal_slot ? timeOfDayLabel(item.meal_slot.timeOfDay) : ''
              }}</template>
              <template v-slot:[`item.type`]="{ item }">{{
                item.meal_slot ? mealTypeLabel[item.meal_slot.type] : ''
              }}</template>
              <template v-slot:[`item.isDefaultRegister`]="{ item }">
                <v-chip x-small dark :color="defaultRegisterColor(item.isDefaultRegister)">
                  {{ defaultRegisterLabel(item.isDefaultRegister) }}
                </v-chip>
              </template>
            </v-data-table>
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <MealConfigHistory v-if="group.category === 'custom'" ref="history" :group-id="group.id" />

      <AddStudentDialog v-if="group.id" ref="addDialog" :group-id="group.id" @added="onMembersChanged" />
      <ImportExcelDialog v-if="group.id" ref="importDialog" :group-id="group.id" @added="onMembersChanged" />
    </div>
    <Page403 v-else />
  </div>
</template>

<script>
import Breadcrumbs from '@/components/layout/Breadcrumbs'
import Page403 from '@/views/Page403.vue'
import CardStudentName from '@/components/basic/card/CardStudentName.vue'
import AddStudentDialog from '@/modules/mealConfig/dialogAddStudent/AddStudentDialog'
import ImportExcelDialog from '@/modules/mealConfig/dialogAddStudent/ImportExcelDialog'
import MealConfigHistory from '@/modules/mealConfig/MealConfigHistory'
import { MealGroupConfig } from '@/plugins/api'
import { listGroupStudents } from '@/store/mealConfig/helpers/groupStudents.js'
import { getHeader } from '@/modules/tuitionFee/detail-group/helper/headerTable.js'
import {
  timeOfDayLabel,
  MEAL_TYPE_LABEL,
  groupCategoryLabel,
  defaultRegisterLabel,
  defaultRegisterColor
} from '@/modules/mealConfig/mealConfigConstants'
import utils from '@/plugins/utils'
import { mapActions, mapState } from 'vuex'

export default {
  components: { Breadcrumbs, Page403, CardStudentName, AddStudentDialog, ImportExcelDialog, MealConfigHistory },
  data() {
    return {
      group: {},
      tab: 0,
      options: {},
      appliedMeals: [],
      loadingMeals: false,
      infoDialog: false,
      renameDialog: false,
      renameTitle: '',
      savingRename: false,
      mealTypeLabel: MEAL_TYPE_LABEL,
      mealHeaders: [
        { text: 'Tên bữa', value: 'meal', sortable: false },
        { text: 'Thời điểm', value: 'timeOfDay', sortable: false },
        { text: 'Loại', value: 'type', sortable: false },
        { text: 'Mặc định', value: 'isDefaultRegister', sortable: false }
      ]
    }
  },
  computed: {
    ...mapState('mealGroupStudent', ['students', 'totalItems', 'loading']),
    studentHeaders() {
      return getHeader(this.group.category)
    }
  },
  watch: {
    options: {
      handler() {
        if (this.group.id) this.refresh()
      },
      deep: true
    }
  },
  async created() {
    this.group = await this.fetchGroup(this.$route.params.id)
    this.refresh()
    this.fetchAppliedMeals()
  },
  methods: {
    ...mapActions('mealGroup', { fetchGroup: 'fetchOne', removeGroup: 'remove', updateGroup: 'update' }),
    ...mapActions('mealGroupStudent', ['fetchMembers', 'remove']),
    timeOfDayLabel,
    groupCategoryLabel,
    defaultRegisterLabel,
    defaultRegisterColor,
    openRename() {
      this.infoDialog = false
      this.renameTitle = this.group.title
      this.renameDialog = true
      this.$nextTick(() => this.$refs.renameForm && this.$refs.renameForm.resetValidation())
    },
    async saveRename() {
      if (!this.$refs.renameForm.validate()) return
      this.savingRename = true
      try {
        const res = await this.updateGroup({ id: this.group.id, title: this.renameTitle.trim() })
        this.group = { ...this.group, title: res.title || this.renameTitle.trim() }
        this.$alert.updateSuccess()
        this.renameDialog = false
        if (this.$refs.history) this.$refs.history.refresh()
      } catch (e) {
        this.$alert.error('Đổi tên thất bại: ' + e)
      } finally {
        this.savingRename = false
      }
    },
    refresh() {
      return this.fetchMembers({
        group: this.group,
        page: this.options.page || 1,
        itemsPerPage: this.options.itemsPerPage
      }).catch(e => this.$alert.error('Không tải được danh sách học sinh: ' + e))
    },
    // sau khi thêm/bớt thành viên: reload danh sách + refresh lịch sử log (giống màn chi tiết bữa)
    async onMembersChanged() {
      await this.refresh()
      if (this.$refs.history) this.$refs.history.refresh()
    },
    async fetchAppliedMeals() {
      this.loadingMeals = true
      try {
        this.appliedMeals = await MealGroupConfig.fetch({ meal_group: this.$route.params.id, _limit: -1 })
      } catch (e) {
        this.$alert.error('Không tải được bữa áp dụng: ' + e)
      } finally {
        this.loadingMeals = false
      }
    },
    async exportExcel() {
      this.$loading.active = true
      try {
        const list = await listGroupStudents(this.group, { _start: 0, _limit: -1 })
        const data = list.map((s, i) => ({
          stt: i + 1,
          code: s.code,
          name: s.name,
          dob: s.dob ? this.$options.filters.ddmmyyyy(s.dob) : '',
          gender: this.$options.filters.gender(s.gender),
          currentClass: s.currentClass ? s.currentClass.title : '',
          status: this.$options.filters.status(s.status)
        }))
        const header = [
          { text: 'STT', value: 'stt' },
          { text: 'Mã số', value: 'code' },
          { text: 'Tên học sinh', value: 'name' },
          { text: 'Ngày sinh', value: 'dob' },
          { text: 'Giới tính', value: 'gender' },
          { text: 'Lớp', value: 'currentClass' },
          { text: 'Trạng thái', value: 'status' }
        ]
        utils.exportExcel(data, header, `Học sinh nhóm ${this.group.title}`)
      } catch (e) {
        this.$alert.error('Xuất Excel thất bại: ' + e)
      } finally {
        this.$loading.active = false
      }
    },
    confirmRemove(item) {
      this.$dialog.confirm({
        title: 'Xác nhận',
        text: `Gỡ học sinh ${item.name} khỏi nhóm?`,
        okText: 'Gỡ',
        cancelText: 'Hủy',
        done: async () => {
          this.$loading.active = true
          try {
            await this.remove(item.groupStudentId)
            this.$alert.deleteSuccess()
            await this.onMembersChanged()
          } catch (e) {
            this.$alert.deleteError(e)
          } finally {
            this.$loading.active = false
          }
        }
      })
    },
    confirmDeleteGroup() {
      this.$dialog.confirm({
        title: 'Xác nhận xóa nhóm',
        text: `Xóa nhóm "${this.group.title}"? Thao tác không thể hoàn tác.`,
        okText: 'Xóa',
        cancelText: 'Hủy',
        done: async () => {
          this.$loading.active = true
          try {
            await this.removeGroup(this.$route.params.id)
            this.$alert.deleteSuccess('nhóm đối tượng')
            this.$router.push('/meal-group')
          } catch (e) {
            this.$alert.deleteError(e)
          } finally {
            this.$loading.active = false
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.info-label {
  font-size: 14px;
  color: #797979;
}
.info-value {
  font-size: 18px;
  font-weight: 500;
  color: #000;
}
</style>
